/**
 * PortalWebhookTester — Webhook testing tool
 *
 * Lets users paste a webhook URL, pick a vertical, fire a realistic
 * test payload via a Supabase Edge Function (to dodge CORS), and see
 * the response with status code, latency, and JSON body.
 *
 * History of last 5 tests is kept in component state.
 */

import React, { useState, useEffect, useCallback } from 'react';
import PortalLayout from '@/components/portal/PortalLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabaseGet } from '@/utils/supabaseRest';
import {
  Webhook,
  Send,
  Loader2,
  Clock,
  CheckCircle2,
  XCircle,
  Copy,
  Trash2,
} from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────────

interface Vertical {
  id: string;
  name: string;
  cluster: string;
  is_active: boolean;
}

interface TestResult {
  id: string;
  url: string;
  vertical: string;
  status_code: number;
  response_body: string;
  latency_ms: number;
  timestamp: string;
}

// ── Constants ─────────────────────────────────────────────────────────

const SUPABASE_URL =
  import.meta.env?.VITE_SUPABASE_URL || 'https://khwvlnkrtrwvfbnnjsrc.supabase.co';

function buildTestPayload(verticalName: string) {
  return {
    test_mode: true,
    source: 'intentdrive_webhook_test',
    session_id: `test-${Date.now()}`,
    vertical: verticalName.toLowerCase().replace(/\s+/g, '_'),
    lead: {
      first_name: 'Test',
      last_name: 'User',
      email: 'test@intentdrive.ai',
      phone: '+15551234567',
      state: 'CA',
      zip: '90210',
    },
    profile: {
      homeowner: true,
      credit_tier: 'good',
      age_range: '35_44',
      debt_flag: false,
    },
    metadata: {
      entry_intent: verticalName.toLowerCase().replace(/\s+/g, '_'),
      session_value: 0,
      phase: 1,
    },
  };
}

function getAuthHeaders(): Record<string, string> {
  const tokenRaw = localStorage.getItem('supabase.auth.token');
  const accessToken = tokenRaw ? JSON.parse(tokenRaw).access_token : null;
  const anonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY || '';
  return {
    'Content-Type': 'application/json',
    apikey: anonKey,
    Authorization: accessToken ? `Bearer ${accessToken}` : `Bearer ${anonKey}`,
  };
}

function StatusBadge({ code }: { code: number }) {
  if (code === 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono bg-slate-700 text-slate-300">
        ERR
      </span>
    );
  }
  const is2xx = code >= 200 && code < 300;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono ${
        is2xx ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
      }`}
    >
      {is2xx ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
      {code}
    </span>
  );
}

function LatencyBadge({ ms }: { ms: number }) {
  const color = ms < 500 ? 'text-emerald-400' : ms < 2000 ? 'text-yellow-400' : 'text-red-400';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono bg-slate-700 ${color}`}>
      <Clock className="h-3 w-3" />
      {ms}ms
    </span>
  );
}

function tryFormatJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

// ── Component ─────────────────────────────────────────────────────────

const PortalWebhookTester: React.FC = () => {
  const [webhookUrl, setWebhookUrl] = useState('');
  const [verticals, setVerticals] = useState<Vertical[]>([]);
  const [selectedVertical, setSelectedVertical] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingVerticals, setLoadingVerticals] = useState(true);
  const [history, setHistory] = useState<TestResult[]>([]);
  const [copied, setCopied] = useState<string | null>(null);

  // Fetch verticals on mount
  useEffect(() => {
    (async () => {
      try {
        const data = await supabaseGet<Vertical[]>(
          'verticals?select=id,name,cluster,is_active&is_active=eq.true&order=name.asc'
        );
        setVerticals(data);
        if (data.length > 0) setSelectedVertical(data[0].name);
      } catch (err) {
        console.error('Failed to load verticals:', err);
      } finally {
        setLoadingVerticals(false);
      }
    })();
  }, []);

  const handleSendTest = useCallback(async () => {
    if (!webhookUrl || !selectedVertical) return;
    setLoading(true);

    const payload = buildTestPayload(selectedVertical);

    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/webhook-test`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ url: webhookUrl, payload }),
      });

      const data = await res.json();

      const result: TestResult = {
        id: crypto.randomUUID(),
        url: webhookUrl,
        vertical: selectedVertical,
        status_code: data.status_code ?? 0,
        response_body: data.response_body ?? data.error ?? 'No response',
        latency_ms: data.latency_ms ?? 0,
        timestamp: new Date().toISOString(),
      };

      setHistory((prev) => [result, ...prev].slice(0, 5));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setHistory((prev) =>
        [
          {
            id: crypto.randomUUID(),
            url: webhookUrl,
            vertical: selectedVertical,
            status_code: 0,
            response_body: `Client error: ${message}`,
            latency_ms: 0,
            timestamp: new Date().toISOString(),
          },
          ...prev,
        ].slice(0, 5)
      );
    } finally {
      setLoading(false);
    }
  }, [webhookUrl, selectedVertical]);

  const handleCopy = useCallback((text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  }, []);

  // Preview payload
  const previewPayload = selectedVertical ? buildTestPayload(selectedVertical) : null;

  return (
    <PortalLayout>
      <div className="p-6 max-w-5xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Webhook className="h-6 w-6 text-emerald-400" />
            Webhook Tester
          </h1>
          <p className="text-slate-400 mt-1">
            Send test leads to any webhook endpoint and inspect the response.
          </p>
        </div>

        {/* Send Test Section */}
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-5">
          {/* URL Input */}
          <div className="space-y-2">
            <Label className="text-slate-300">Webhook URL</Label>
            <Input
              placeholder="https://your-api.com/webhook/leads"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 font-mono text-sm"
            />
          </div>

          {/* Vertical Select */}
          <div className="space-y-2">
            <Label className="text-slate-300">Vertical</Label>
            {loadingVerticals ? (
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading verticals...
              </div>
            ) : (
              <select
                value={selectedVertical}
                onChange={(e) => setSelectedVertical(e.target.value)}
                className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                {verticals.map((v) => (
                  <option key={v.id} value={v.name}>
                    {v.name} ({v.cluster})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Send Button */}
          <Button
            onClick={handleSendTest}
            disabled={loading || !webhookUrl || !selectedVertical}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Test Lead
              </>
            )}
          </Button>
        </div>

        {/* Payload Preview */}
        {previewPayload && (
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wide">
                Payload Preview
              </h2>
              <button
                onClick={() => handleCopy(JSON.stringify(previewPayload, null, 2), 'preview')}
                className="text-slate-500 hover:text-emerald-400 transition-colors"
                title="Copy payload"
              >
                <Copy className="h-4 w-4" />
                {copied === 'preview' && (
                  <span className="ml-1 text-xs text-emerald-400">Copied</span>
                )}
              </button>
            </div>
            <pre className="bg-slate-950 border border-slate-800 rounded p-4 text-xs font-mono text-slate-300 overflow-x-auto max-h-64 overflow-y-auto">
              {JSON.stringify(previewPayload, null, 2)}
            </pre>
          </div>
        )}

        {/* History */}
        {history.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Test History</h2>
              <button
                onClick={() => setHistory([])}
                className="text-slate-500 hover:text-red-400 transition-colors text-sm flex items-center gap-1"
              >
                <Trash2 className="h-3 w-3" />
                Clear
              </button>
            </div>

            {history.map((result) => (
              <div
                key={result.id}
                className="bg-slate-900 border border-slate-800 rounded-lg p-5 space-y-3"
              >
                {/* Header row */}
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <StatusBadge code={result.status_code} />
                    <LatencyBadge ms={result.latency_ms} />
                    <span className="text-xs text-slate-500 font-mono">
                      {result.vertical}
                    </span>
                  </div>
                  <span className="text-xs text-slate-600">
                    {new Date(result.timestamp).toLocaleTimeString()}
                  </span>
                </div>

                {/* URL */}
                <p className="text-xs text-slate-500 font-mono truncate">
                  POST {result.url}
                </p>

                {/* Response Body */}
                <div className="relative">
                  <button
                    onClick={() =>
                      handleCopy(tryFormatJson(result.response_body), result.id)
                    }
                    className="absolute top-2 right-2 text-slate-600 hover:text-emerald-400 transition-colors"
                    title="Copy response"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                  {copied === result.id && (
                    <span className="absolute top-2 right-8 text-xs text-emerald-400">
                      Copied
                    </span>
                  )}
                  <pre className="bg-slate-950 border border-slate-800 rounded p-4 text-xs font-mono text-slate-300 overflow-x-auto max-h-48 overflow-y-auto">
                    {tryFormatJson(result.response_body)}
                  </pre>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </PortalLayout>
  );
};

export default PortalWebhookTester;
